import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import { Theme } from '@seedlang/constants';
import Modal from 'components/modal';
import Button from 'components/button/button';
import Spinner from 'components/spinner';
import { gaps } from 'components/layout/stack';
import eventParamsHelper from 'utils/event_params_helper';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  padding: 10px;
  p {
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  font-weight: bold;
  font-size: 14px;
  display: block;
  margin-bottom: 3px;
`;

const FieldValue = styled.div`
  font-size: 16px;
`;

const NameInput = styled.input`
  width: 100%;
  font-size: 16px;
  padding: 8px 10px;
  border: 1px solid #CCC;
  border-radius: 5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${gaps.sm};

  & > * {
    width: 100%;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #222;
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  color: ${Theme.red};
  font-size: 14px;
`;

// Two-step EU withdrawal flow ("Widerrufsbutton", § 356a BGB). The two-step
// design and the exact "Confirm withdrawal" label are legally mandatory.
@observer
class WithdrawalModal extends React.Component {
  @observable step = 'explanation';
  // null until the user edits the field; the displayed value falls back to the
  // (possibly still loading) user profile name, so the form stays pre-filled.
  @observable name = null;
  @observable submitting = false;
  @observable error = null;

  get currentName() {
    return this.name !== null ? this.name : (AppUI.user.name || '');
  }

  onConfirmWithdrawal = () => {
    if (this.submitting) return;
    this.error = null;
    const name = this.currentName.trim();
    if (name === '') {
      this.error = 'Please provide your name.';
      return;
    }
    this.submitting = true;
    AppUI.subscriptionStore.withdraw({
      ids: { subscriptionId: this.props.subscription.id },
      data: { name },
    }, this.afterWithdraw);
  }

  afterWithdraw = (resp) => {
    this.submitting = false;
    if (resp['success']) {
      AppUI.createEvent('memrev - request contract withdrawal', eventParamsHelper({
        subscription_id: this.props.subscription.id,
        membership_id: this.props.subscription.membership?.id,
      }));
      this.step = 'success';
      if (this.props.onWithdrawn) {
        this.props.onWithdrawn();
      }
    } else {
      this.error = resp['error'] || 'Something went wrong. Please try again or contact us by email.';
    }
  }

  render() {
    const { subscription, onClose } = this.props;
    const planTitle = subscription.membership?.title?.replace(/<[^>]*>/g, '');

    return (
      <Modal
        onCloseModal={this.submitting ? null : onClose}
        width='100%'
        maxWidth='500px'
        title='Withdraw from Contract'
        background={Theme.red}
        contentBackground='#FFF'
      >
        <Content>
          {
            this.step === 'explanation' &&
            <>
              <p>
                This is not a regular cancellation. By withdrawing, you revoke your
                membership contract entirely under your statutory 14-day right of
                withdrawal. Your payment will be refunded and your membership ends
                immediately. Your free account stays — only the paid membership is
                withdrawn.
              </p>
              <p>
                If you only want to stop your membership from renewing, use
                "Cancel" instead.
              </p>
              <ButtonGroup>
                <Button
                  onClick={() => this.step = 'form'}
                  background={Theme.red}
                  width='100%'
                  height='50px'
                  borderRadius='30px'
                >
                  Continue with Withdrawal
                </Button>
                <LinkButton onClick={onClose}>
                  Go back
                </LinkButton>
              </ButtonGroup>
            </>
          }
          {
            this.step === 'form' &&
            <>
              <p>
                Please confirm the details of your withdrawal declaration. A receipt
                confirmation will be sent to your account email.
              </p>
              <div>
                <FieldLabel htmlFor='withdrawal-name'>Name</FieldLabel>
                <NameInput
                  id='withdrawal-name'
                  type='text'
                  value={this.currentName}
                  onChange={e => this.name = e.target.value}
                />
              </div>
              <div>
                <FieldLabel>Email</FieldLabel>
                <FieldValue>{AppUI.user.email}</FieldValue>
              </div>
              <div>
                <FieldLabel>Membership plan</FieldLabel>
                <FieldValue>{planTitle}</FieldValue>
              </div>
              <div>
                <FieldLabel>Sign-up date</FieldLabel>
                <FieldValue>{subscription.createdAt?.formattedDateWithYear}</FieldValue>
              </div>
              {
                this.error &&
                <ErrorMessage>{this.error}</ErrorMessage>
              }
              <ButtonGroup>
                <Button
                  onClick={this.onConfirmWithdrawal}
                  background={Theme.red}
                  width='100%'
                  height='50px'
                  borderRadius='30px'
                >
                  {this.submitting ? <Spinner width='50px' height='10px' background='#FFF' margin='0' /> : 'Confirm Withdrawal'}
                </Button>
                {
                  !this.submitting &&
                  <LinkButton onClick={onClose}>
                    Go back
                  </LinkButton>
                }
              </ButtonGroup>
            </>
          }
          {
            this.step === 'success' &&
            <>
              <p>
                We have received your withdrawal declaration. Your membership has
                ended and your account continues as a free account.
              </p>
              <p>
                A receipt confirmation has been sent to {AppUI.user.email}. We will
                process your withdrawal and any refund within 14 days.
              </p>
              <ButtonGroup>
                <Button
                  onClick={onClose}
                  background={AppUI.site.accentColor || Theme.blue}
                  width='100%'
                  height='50px'
                  borderRadius='30px'
                >
                  Close
                </Button>
              </ButtonGroup>
            </>
          }
        </Content>
      </Modal>
    );
  }
}

export default WithdrawalModal;
